import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../api/client";
import { PART_CLASSES, type PartDetail } from "../../api/types";
import { S } from "../../styles";
import { LoadingState } from "../../components/QueryStates";

const LIMIT = 20;

/** The sentinel the API accepts for "class was never recorded".
 *  Mirrors PART_CLASS_UNKNOWN in app/repositories/part.py. */
const UNKNOWN = "unknown";

/** ATA chapters the autonomous ordering policy treats as export-control
 *  sensitive. Mirrors `_ITAR_ATA_CHAPTERS` in
 *  app/services/autonomous_order_service.py — display only, the block itself is
 *  enforced server-side. It is a deliberately conservative superset, not an
 *  ECCN/USML determination, and the tooltip says so. */
const ITAR_CHAPTERS: Record<string, string> = {
  "22": "Auto Flight",
  "23": "Communications",
  "27": "Flight Controls",
  "31": "Indicating / Recording",
  "34": "Navigation",
  "42": "Integrated Modular Avionics",
};

/** What each class means for the paperwork a part has to arrive with. */
const CLASS_HINTS: Record<string, string> = {
  rotable:
    "Serialized, repaired and returned to service — airworthiness release expected.",
  repairable: "Repaired rather than scrapped — airworthiness release expected.",
  expendable: "Consumed in use, not repaired — conformity evidence expected.",
  consumable: "Bulk material consumed in use — conformity evidence expected.",
  standard:
    "Standard hardware to a published spec — conformity evidence expected.",
};

const CLASS_COLORS: Record<string, { bg: string; fg: string }> = {
  rotable: { bg: "#e0e7ff", fg: "#3730a3" },
  repairable: { bg: "#ede9fe", fg: "#5b21b6" },
  expendable: { bg: "#d1fae5", fg: "#065f46" },
  consumable: { bg: "#ccfbf1", fg: "#115e59" },
  standard: { bg: "#e2e8f0", fg: "#334155" },
};

/** Reduce a stored chapter to its canonical two-digit form.
 *  Mirrors `normalize_ata_chapter` in app/services/autonomous_order_service.py:
 *  "32", "ATA 32" and "32-41-00" all reduce to "32". */
export function canonicalChapter(value: string | null): string {
  if (!value) return "";
  const match = /\d+/.exec(value);
  if (!match) return "";
  const digits = match[0].replace(/^0+/, "") || "0";
  return digits.length > 2 ? "" : digits.padStart(2, "0");
}

function ClassBadge({ partClass }: { partClass: string | null }) {
  if (!partClass) {
    return (
      <span
        title={
          "No class recorded. The trace requirement falls back to the condition " +
          "code alone, and an autonomous order is routed to a human however the " +
          "eligibility allowlist is set."
        }
        style={{
          padding: "2px 8px",
          borderRadius: 10,
          fontSize: 11.5,
          fontWeight: 600,
          background: "#fef3c7",
          color: "#92400e",
          whiteSpace: "nowrap",
        }}
      >
        ⚠ not recorded
      </span>
    );
  }
  const key = partClass.trim().toLowerCase();
  const c = CLASS_COLORS[key] ?? { bg: "#f1f5f9", fg: "#475569" };
  return (
    <span
      title={CLASS_HINTS[key] ?? "Class not in the standard vocabulary."}
      style={{
        padding: "2px 8px",
        borderRadius: 10,
        fontSize: 11.5,
        fontWeight: 600,
        background: c.bg,
        color: c.fg,
        whiteSpace: "nowrap",
      }}
    >
      {key}
    </span>
  );
}

function ChapterCell({ part }: { part: PartDetail }) {
  const canonical = canonicalChapter(part.ata_chapter);
  if (!part.ata_chapter) {
    return <span style={{ color: "#b45309" }}>⚠ none</span>;
  }
  const system = ITAR_CHAPTERS[canonical];
  return (
    <span style={{ whiteSpace: "nowrap" }}>
      <span style={{ fontVariantNumeric: "tabular-nums" }}>
        {part.ata_chapter}
      </span>
      {canonical && canonical !== part.ata_chapter && (
        <span style={{ color: "#94a3b8", marginLeft: 6, fontSize: 12 }}>
          (ch {canonical})
        </span>
      )}
      {system && (
        <span
          title={
            `Chapter ${canonical} — ${system}. The autonomous ordering policy ` +
            "blocks these chapters when the ITAR block is on. It is a conservative " +
            "proxy, not an ECCN/USML determination."
          }
          style={{
            marginLeft: 6,
            padding: "1px 6px",
            borderRadius: 8,
            fontSize: 10.5,
            fontWeight: 700,
            background: "#fee2e2",
            color: "#991b1b",
          }}
        >
          ITAR
        </span>
      )}
    </span>
  );
}

function CoverageChip({
  label,
  count,
  active,
  warn = false,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  warn?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "baseline",
        gap: 6,
        padding: "6px 12px",
        borderRadius: 10,
        cursor: "pointer",
        fontSize: 12.5,
        fontWeight: 600,
        border: active ? "1.5px solid #4f46e5" : "1px solid #e5e7eb",
        background: active ? "#eef2ff" : warn && count > 0 ? "#fffbeb" : "#fff",
        color: warn && count > 0 ? "#92400e" : "#334155",
      }}
    >
      <span>{label}</span>
      <span style={{ fontSize: 14, fontWeight: 700 }}>{count}</span>
    </button>
  );
}

export function PartsPanel() {
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");
  const [partClass, setPartClass] = useState("");
  const [chapter, setChapter] = useState("");
  const [activeOnly, setActiveOnly] = useState(false);
  const [skip, setSkip] = useState(0);

  // Debounced so a search runs on the phrase, not on each keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setQ(qInput.trim());
      setSkip(0);
    }, 300);
    return () => clearTimeout(t);
  }, [qInput]);

  const filters = {
    skip,
    limit: LIMIT,
    q: q || undefined,
    part_class: partClass || undefined,
    ata_chapter: chapter.trim() || undefined,
    is_active: activeOnly ? true : undefined,
  };

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["parts-browse", filters],
    queryFn: () => api.listPartsDetail(filters),
  });

  const { data: coverage } = useQuery({
    queryKey: ["parts-class-coverage"],
    queryFn: api.getPartClassCoverage,
  });

  const parts = data?.items ?? [];
  const total = data?.total ?? 0;
  const unknownCount = coverage?.[UNKNOWN] ?? 0;

  const applyClass = (value: string) => {
    setPartClass((current) => (current === value ? "" : value));
    setSkip(0);
  };

  const clearFilters = () => {
    setQInput("");
    setQ("");
    setPartClass("");
    setChapter("");
    setActiveOnly(false);
    setSkip(0);
  };

  const filtered = Boolean(q || partClass || chapter.trim() || activeOnly);

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 8,
          flexWrap: "wrap",
          gap: 10,
        }}
      >
        <h2 style={{ ...S.h2, margin: 0 }}>
          🔧 Part Master{" "}
          <span style={{ fontSize: 14, fontWeight: 400, color: "#888" }}>
            ({total} {filtered ? "matching" : "total"})
          </span>
        </h2>
        <button style={S.btnSm} onClick={() => void refetch()}>
          ↺ Refresh
        </button>
      </div>

      <p style={{ ...S.muted, marginTop: 0, marginBottom: 16 }}>
        Part class decides what paperwork a part must arrive with, and which
        parts the autonomous ordering policy may buy unattended. ATA chapter is
        separate: it is what the policy's ITAR block reads.
      </p>

      {/* Class coverage — click a chip to filter by that class */}
      {coverage && (
        <div
          style={{
            display: "flex",
            gap: 8,
            flexWrap: "wrap",
            marginBottom: 14,
            alignItems: "center",
          }}
        >
          {PART_CLASSES.map((c) => (
            <CoverageChip
              key={c}
              label={c}
              count={coverage[c] ?? 0}
              active={partClass === c}
              onClick={() => applyClass(c)}
            />
          ))}
          <CoverageChip
            label="no class recorded"
            count={unknownCount}
            active={partClass === UNKNOWN}
            warn
            onClick={() => applyClass(UNKNOWN)}
          />
        </div>
      )}

      {unknownCount > 0 && (
        <p
          style={{
            ...S.muted,
            marginTop: 0,
            marginBottom: 16,
            color: "#92400e",
          }}
        >
          ⚠ {unknownCount} part{unknownCount === 1 ? "" : "s"} in this tenant
          have no class recorded. Quotes for them are routed to a human whatever
          the autonomous eligibility allowlist says.
        </p>
      )}

      {/* Filters */}
      <div
        style={{
          display: "flex",
          gap: 10,
          flexWrap: "wrap",
          alignItems: "center",
          marginBottom: 14,
        }}
      >
        <input
          style={{ ...S.input, width: 260, marginBottom: 0 }}
          type="text"
          placeholder="Search part number or description"
          value={qInput}
          onChange={(e) => setQInput(e.target.value)}
        />
        <select
          style={{ ...S.select, width: "auto", marginBottom: 0 }}
          value={partClass}
          onChange={(e) => {
            setPartClass(e.target.value);
            setSkip(0);
          }}
        >
          <option value="">All part classes</option>
          {PART_CLASSES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
          <option value={UNKNOWN}>no class recorded</option>
        </select>
        <input
          style={{ ...S.input, width: 170, marginBottom: 0 }}
          type="text"
          placeholder="ATA chapter e.g. 32"
          value={chapter}
          onChange={(e) => {
            setChapter(e.target.value);
            setSkip(0);
          }}
        />
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: 13,
          }}
        >
          <input
            type="checkbox"
            checked={activeOnly}
            onChange={(e) => {
              setActiveOnly(e.target.checked);
              setSkip(0);
            }}
          />
          Active only
        </label>
        {filtered && (
          <button style={S.btnGhost} onClick={clearFilters}>
            Clear filters
          </button>
        )}
      </div>

      {error && (
        <div style={S.card}>
          <p style={S.err}>⚠ {error.message}</p>
        </div>
      )}

      {isLoading && <LoadingState label="Loading parts…" />}

      {!isLoading && !error && parts.length === 0 && (
        <div style={S.card}>
          <p style={S.muted}>
            {filtered
              ? "No parts match these filters."
              : "No parts in the master yet."}
          </p>
        </div>
      )}

      {parts.length > 0 && (
        <div style={{ ...S.card, padding: 0, overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: 13.5,
            }}
          >
            <thead>
              <tr>
                {[
                  "Part Number",
                  "Description",
                  "Part Class",
                  "ATA Chapter",
                  "Life-Limited",
                  "UoM",
                  "Status",
                ].map((h) => (
                  <th
                    key={h}
                    style={{
                      textAlign: "left",
                      padding: "9px 12px",
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: "0.04em",
                      textTransform: "uppercase",
                      color: "#94a3b8",
                      borderBottom: "1px solid #e6e9f0",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {parts.map((p) => (
                <tr key={p.id}>
                  <td
                    style={{
                      padding: "9px 12px",
                      borderBottom: "1px solid #f1f4f9",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {p.part_number}
                  </td>
                  <td
                    style={{
                      padding: "9px 12px",
                      borderBottom: "1px solid #f1f4f9",
                      color: "#475569",
                    }}
                  >
                    {p.description}
                  </td>
                  <td
                    style={{
                      padding: "9px 12px",
                      borderBottom: "1px solid #f1f4f9",
                    }}
                  >
                    <ClassBadge partClass={p.part_class} />
                  </td>
                  <td
                    style={{
                      padding: "9px 12px",
                      borderBottom: "1px solid #f1f4f9",
                    }}
                  >
                    <ChapterCell part={p} />
                  </td>
                  <td
                    style={{
                      padding: "9px 12px",
                      borderBottom: "1px solid #f1f4f9",
                    }}
                  >
                    {p.is_life_limited ? (
                      <span
                        title="Needs a back-to-birth record on top of whatever the condition code demands."
                        style={{ color: "#b45309", fontWeight: 600 }}
                      >
                        yes
                      </span>
                    ) : (
                      <span style={{ color: "#94a3b8" }}>no</span>
                    )}
                  </td>
                  <td
                    style={{
                      padding: "9px 12px",
                      borderBottom: "1px solid #f1f4f9",
                      color: "#475569",
                    }}
                  >
                    {p.unit_of_measure}
                  </td>
                  <td
                    style={{
                      padding: "9px 12px",
                      borderBottom: "1px solid #f1f4f9",
                    }}
                  >
                    {p.is_active ? (
                      <span style={{ color: "#059669" }}>active</span>
                    ) : (
                      <span style={{ color: "#94a3b8" }}>inactive</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {total > LIMIT && (
        <div
          style={{
            display: "flex",
            gap: 8,
            justifyContent: "flex-end",
            marginTop: 12,
            alignItems: "center",
          }}
        >
          <button
            style={S.btnGhost}
            disabled={skip === 0}
            onClick={() => setSkip(Math.max(0, skip - LIMIT))}
          >
            ← Prev
          </button>
          <span style={S.muted}>
            {skip + 1}–{Math.min(skip + LIMIT, total)} of {total}
          </span>
          <button
            style={S.btnGhost}
            disabled={skip + LIMIT >= total}
            onClick={() => setSkip(skip + LIMIT)}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
